/**
 * ZustandBridge - ZustandストアとPhaserシーンの連携
 * ゲーム状態の同期、カードクリックイベントの伝達を担当
 */
import { useGameStore } from '../../stores/gameStore';
import { useUIStore } from '../../stores/uiStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useNetworkStore } from '../../stores/networkStore';
import { MainGameScene } from '../scenes/MainGameScene';
import { CardColor, CardType } from '../../types/card';
import { getSoundManager } from '@/services/SoundManager';
import { ANIMATION_CONFIG } from '../../utils/constants';

export class ZustandBridge {
  private scene: MainGameScene | null = null;
  private unsubscribers: (() => void)[] = [];
  private isConnected: boolean = false;
  private isAnimating: boolean = false;
  private animationBlockTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Phaserシーンと接続
   */
  public connect(scene: MainGameScene): void {
    if (this.isConnected) {
      this.disconnect();
    }

    this.scene = scene;
    this.isConnected = true;

    this.subscribeToGameStore();
    this.subscribeToUIStore();
    this.subscribeToSettingsStore();

    // 初期状態を同期
    this.syncInitialState();
  }

  /**
   * 接続を解除
   */
  public disconnect(): void {
    this.stopAnimationBlock();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
    this.scene = null;
    this.isConnected = false;
  }

  /**
   * クリックブロック開始
   * アニメーション完了通知が欠落した場合に備えてタイムアウト解除を設定
   */
  private startAnimationBlock(): void {
    this.isAnimating = true;
    if (this.animationBlockTimer) {
      clearTimeout(this.animationBlockTimer);
    }
    this.animationBlockTimer = setTimeout(() => {
      this.isAnimating = false;
      this.animationBlockTimer = null;
      console.warn('Animation block timeout reached. Forced input unblock.');
    }, ANIMATION_CONFIG.ANIMATION_TIMEOUT + 500);
  }

  /**
   * クリックブロック解除
   */
  private stopAnimationBlock(): void {
    this.isAnimating = false;
    if (this.animationBlockTimer) {
      clearTimeout(this.animationBlockTimer);
      this.animationBlockTimer = null;
    }
  }

  /**
   * シーンが Phaser によって既に破棄されていないか防御チェック。
   * `gameInstance.destroy(true)` 直後は scene 参照が残っているのに
   * `scene.sys` が undefined になっているタイミングがあり、その状態で
   * scene のメソッドを呼ぶと TypeError が発生する (issue #50)。
   */
  private isSceneAlive(): boolean {
    if (!this.scene) return false;
    const sys = (this.scene as unknown as { sys?: { isActive?: () => boolean } }).sys;
    if (!sys) return false;
    if (typeof sys.isActive === 'function' && !sys.isActive()) return false;
    return true;
  }

  /**
   * gameStoreの変更を購読
   */
  private subscribeToGameStore(): void {
    // ゲーム状態の変更を監視
    const unsubGameState = useGameStore.subscribe((state) => {
      const gameState = state.gameState;
      if (gameState && this.isSceneAlive()) {
        const localPlayerId = state.localPlayerId || '';
        this.scene!.syncWithGameState(gameState, localPlayerId);
      }
    });
    this.unsubscribers.push(unsubGameState);

    // プレイ可能カードの変更を監視（別のsubscription）
    let prevPlayableIds: Set<string> = new Set();
    const unsubPlayable = useGameStore.subscribe((state) => {
      const playableCardIds = state.playableCardIds;
      // 変更があった場合のみ更新
      if (playableCardIds !== prevPlayableIds) {
        prevPlayableIds = playableCardIds;
        if (this.isSceneAlive()) {
          this.scene!.highlightPlayableCards(playableCardIds);
        }
      }
    });
    this.unsubscribers.push(unsubPlayable);
  }

  /**
   * uiStoreの変更を購読
   */
  private subscribeToUIStore(): void {
    // アニメーションキューを監視
    let prevQueue: unknown[] = [];
    const unsubAnimations = useUIStore.subscribe((state) => {
      const animationQueue = state.animationQueue;
      if (animationQueue !== prevQueue) {
        prevQueue = animationQueue;
        this.processAnimationQueue(animationQueue);
      }
    });
    this.unsubscribers.push(unsubAnimations);

    // ラウンド結果モーダルを監視（勝利音）
    let prevRoundResultOpen = false;
    const unsubRoundResult = useUIStore.subscribe((state) => {
      const isOpen = state.modals.roundResult.isOpen;
      if (isOpen && !prevRoundResultOpen) {
        getSoundManager()?.playRoundWin();
      }
      prevRoundResultOpen = isOpen;
    });
    this.unsubscribers.push(unsubRoundResult);

    // ゲームオーバーモーダルを監視
    let prevGameOverOpen = false;
    const unsubGameOver = useUIStore.subscribe((state) => {
      const isOpen = state.modals.gameOver.isOpen;
      if (isOpen && !prevGameOverOpen) {
        getSoundManager()?.playGameOver();
      }
      prevGameOverOpen = isOpen;
    });
    this.unsubscribers.push(unsubGameOver);
  }

  /**
   * settingsStoreの変更を購読（サウンド設定）
   */
  private subscribeToSettingsStore(): void {
    let prevAudio = useSettingsStore.getState().audio;
    const unsubSettings = useSettingsStore.subscribe((state) => {
      const currentAudio = state.audio;
      // オーディオ設定が変更された場合
      if (
        currentAudio.masterVolume !== prevAudio.masterVolume ||
        currentAudio.soundEffectsVolume !== prevAudio.soundEffectsVolume ||
        currentAudio.musicVolume !== prevAudio.musicVolume ||
        currentAudio.soundEnabled !== prevAudio.soundEnabled ||
        currentAudio.musicEnabled !== prevAudio.musicEnabled
      ) {
        prevAudio = currentAudio;
        const soundManager = getSoundManager();
        if (soundManager) {
          soundManager.updateVolumes();
        }
      }
    });
    this.unsubscribers.push(unsubSettings);
  }

  /**
   * 初期状態を同期
   */
  private syncInitialState(): void {
    const gameStore = useGameStore.getState();

    if (gameStore.gameState && this.scene) {
      const localPlayerId = gameStore.localPlayerId || '';
      this.scene.syncWithGameState(gameStore.gameState, localPlayerId);
      this.scene.highlightPlayableCards(gameStore.playableCardIds);
    }
  }

  /**
   * アニメーションキューを処理
   */
  private processAnimationQueue(queue: any[]): void {
    if (!this.scene) return;

    const uiStore = useUIStore.getState();

    queue.forEach((animation) => {
      if (animation.status !== 'pending') return;

      // アニメーション開始をマーク
      uiStore.startAnimation(animation.id);

      // アニメーションタイプに応じて処理
      switch (animation.type) {
        case 'play_card':
          this.handlePlayCardAnimation(animation, () => {
            uiStore.completeAnimation(animation.id);
          });
          break;
        case 'draw_card':
          this.handleDrawCardAnimation(animation, () => {
            uiStore.completeAnimation(animation.id);
          });
          break;
        case 'deal_cards':
          this.handleDealCardsAnimation(animation, () => {
            uiStore.completeAnimation(animation.id);
          });
          break;
        case 'win_celebration':
          this.handleWinCelebration(animation, () => {
            uiStore.completeAnimation(animation.id);
          });
          break;
        default:
          uiStore.completeAnimation(animation.id);
      }
    });
  }

  /**
   * カードプレイアニメーションを処理
   */
  private handlePlayCardAnimation(animation: any, callback: () => void): void {
    if (!this.isSceneAlive()) {
      console.warn('PlayCard animation: scene not available or destroyed');
      callback();
      return;
    }

    const { cardId, playerId } = animation.data || {};
    if (!cardId || !playerId) {
      console.warn('PlayCard animation: missing cardId or playerId', animation.data);
      callback();
      return;
    }

    this.startAnimationBlock();
    console.log(`Playing card animation: ${cardId} from player ${playerId}`);

    // カードプレイ音を再生
    getSoundManager()?.playCardPlay();

    this.scene!.playCardAnimation(cardId, playerId, () => {
      this.stopAnimationBlock();
      callback();
    });
  }

  /**
   * カードドローアニメーションを処理
   */
  private handleDrawCardAnimation(animation: any, callback: () => void): void {
    if (!this.isSceneAlive()) {
      console.warn('DrawCard animation: scene not available or destroyed');
      callback();
      return;
    }

    const { playerId, card } = animation.data || {};
    if (!playerId || !card) {
      console.warn('DrawCard animation: missing playerId or card', animation.data);
      callback();
      return;
    }

    this.startAnimationBlock();
    console.log(`Drawing card animation: player ${playerId}`);

    // カードドロー音を再生
    getSoundManager()?.playCardDraw();

    this.scene!.drawCardAnimation(playerId, card, () => {
      this.stopAnimationBlock();
      callback();
    });
  }

  /**
   * カード配布アニメーションを処理
   */
  private handleDealCardsAnimation(animation: any, callback: () => void): void {
    if (!this.isSceneAlive()) {
      console.warn('DealCards animation: scene not available or destroyed');
      callback();
      return;
    }

    // アニメーションデータから取得、なければgameStoreから取得
    const { players, localPlayerId } = animation.data || {};
    const gameStore = useGameStore.getState();
    const finalPlayers = players || gameStore.gameState?.players || [];
    const finalLocalPlayerId = localPlayerId || gameStore.localPlayerId || '';

    if (finalPlayers.length === 0) {
      console.warn('DealCards animation: no players');
      callback();
      return;
    }

    this.startAnimationBlock();
    console.log(`Dealing cards animation: ${finalPlayers.length} players`);

    // カード配布音を再生
    getSoundManager()?.playCardDeal();

    this.scene!.dealCardsAnimation(finalPlayers, finalLocalPlayerId, () => {
      this.stopAnimationBlock();
      callback();
    });
  }

  /**
   * 勝利セレブレーションを処理
   */
  private handleWinCelebration(animation: { data?: { winningTeam?: string } }, callback: () => void): void {
    if (!this.isSceneAlive()) {
      callback();
      return;
    }

    const winningTeam = animation.data?.winningTeam || 'unknown';
    this.scene!.showWinCelebration(winningTeam);

    // 少し遅延してからコールバック
    setTimeout(callback, 2000);
  }

  /**
   * カードクリックイベント
   */
  public onCardClicked(cardId: string): void {
    const gameStore = useGameStore.getState();
    const uiStore = useUIStore.getState();
    const networkStore = useNetworkStore.getState();

    // アニメーション中はカードクリックを無視
    if (this.isAnimating) {
      console.log('Card click blocked: animation in progress');
      return;
    }

    // ターン遷移中はカードクリックを無視
    if (uiStore.isTransitionBlocking()) {
      console.log('Card click blocked: transition in progress');
      return;
    }

    const localPlayerId = gameStore.localPlayerId;
    if (!localPlayerId) return;

    // プレイ可能か確認
    if (!gameStore.playableCardIds.has(cardId)) return;

    // カードを取得
    const card = gameStore.getPlayerHand(localPlayerId).find((c) => c.id === cardId);
    if (!card) return;

    // ワイルドカードの場合は色選択モーダルを表示
    const needsColor =
      card.type === CardType.WILD || card.type === CardType.WILD_DRAW_FOUR;

    if (needsColor) {
      uiStore.openColorPicker((color: CardColor) => {
        // オンラインモード: サーバーに送信
        if (gameStore.mode === 'online') {
          networkStore.sendPlayCard(cardId, color);
        } else {
          gameStore.playCard(localPlayerId, cardId, color);
        }
      });
    } else {
      // オンラインモード: サーバーに送信
      if (gameStore.mode === 'online') {
        networkStore.sendPlayCard(cardId);
      } else {
        gameStore.playCard(localPlayerId, cardId);
      }
    }
  }

  /**
   * デッキクリックイベント
   */
  public onDeckClicked(): void {
    // アニメーション中はデッキクリックを無視
    if (this.isAnimating) {
      console.log('Deck click blocked: animation in progress');
      return;
    }

    const gameStore = useGameStore.getState();
    const networkStore = useNetworkStore.getState();
    const localPlayerId = gameStore.localPlayerId;

    if (!localPlayerId) return;

    // 現在のプレイヤーか確認
    const currentPlayer = gameStore.getCurrentPlayer();
    if (!currentPlayer || currentPlayer.id !== localPlayerId) return;

    // オンラインモード: サーバーに送信
    if (gameStore.mode === 'online') {
      networkStore.sendDrawCard();
    } else {
      gameStore.drawCard(localPlayerId);
    }
  }
}
